import type { PatchConnectionV1, PatchEndpoint, PatchGraphV1, PatchNodeV1 } from '../../routing-graph/src/index.js';

export interface TemplateParameterRange { readonly min: number; readonly max: number }
export interface PatchTemplateSlotV1 {
  readonly nodeId: string;
  readonly moduleId: string;
  readonly required: boolean;
  readonly parameterRanges: Readonly<Record<string, TemplateParameterRange>>;
}
export interface PatchTemplateConnectionV1 {
  readonly source: PatchEndpoint;
  readonly target: PatchEndpoint;
  readonly required: boolean;
}
export interface PatchTemplateV1 {
  readonly schemaVersion: 1;
  readonly id: string;
  readonly slots: readonly PatchTemplateSlotV1[];
  readonly connections: readonly PatchTemplateConnectionV1[];
}
export interface TemplateValidationReadback {
  readonly templateId: string;
  readonly patchId: string;
  readonly populatedSlots: readonly string[];
}

const endpointKey = (source: PatchEndpoint, target: PatchEndpoint): string =>
  `${source.nodeId}:${source.portId}>${target.nodeId}:${target.portId}`;

export const validatePatchAgainstTemplate = (
  template: PatchTemplateV1,
  patch: PatchGraphV1,
): TemplateValidationReadback => {
  if (template.schemaVersion !== 1) throw new RangeError('unsupported template schemaVersion');
  const slots = new Map<string, PatchTemplateSlotV1>();
  for (const slot of template.slots) {
    if (slots.has(slot.nodeId)) throw new RangeError(`duplicate template slot: ${slot.nodeId}`);
    slots.set(slot.nodeId, slot);
    for (const [parameter, range] of Object.entries(slot.parameterRanges)) {
      if (!Number.isFinite(range.min) || !Number.isFinite(range.max) || range.min > range.max) {
        throw new RangeError(`invalid template range for ${slot.nodeId}.${parameter}`);
      }
    }
  }
  const nodes = new Map(patch.nodes.map((node) => [node.id, node]));
  for (const slot of template.slots) {
    const node = nodes.get(slot.nodeId);
    if (slot.required && !node) throw new RangeError(`required template slot is missing: ${slot.nodeId}`);
  }
  for (const node of patch.nodes) {
    const slot = slots.get(node.id);
    if (!slot) throw new RangeError(`node is not allowed by template: ${node.id}`);
    if (node.moduleId !== slot.moduleId) throw new RangeError(`module is not allowed in slot ${node.id}`);
    for (const [parameter, value] of Object.entries(node.parameters)) {
      const range = slot.parameterRanges[parameter];
      if (range && (value < range.min || value > range.max)) {
        throw new RangeError(`${node.id}.${parameter} is outside template range`);
      }
    }
  }
  const allowedConnections = new Map(template.connections.map((connection) => [endpointKey(connection.source, connection.target), connection]));
  const patchConnections = new Set<string>();
  for (const connection of patch.connections) {
    const key = endpointKey(connection.source, connection.target);
    if (!allowedConnections.has(key)) throw new RangeError(`connection is not allowed by template: ${connection.id}`);
    patchConnections.add(key);
  }
  for (const connection of template.connections) {
    if (connection.required && !patchConnections.has(endpointKey(connection.source, connection.target))) {
      throw new RangeError('required template connection is missing');
    }
  }
  return Object.freeze({
    templateId: template.id,
    patchId: patch.id,
    populatedSlots: Object.freeze(patch.nodes.map((node) => node.id)),
  });
};

const cloneEndpoint = (endpoint: PatchEndpoint): PatchEndpoint => ({ nodeId: endpoint.nodeId, portId: endpoint.portId });
const canonicalNode = (node: PatchNodeV1): PatchNodeV1 => ({
  id: node.id,
  moduleId: node.moduleId,
  parameters: Object.fromEntries(Object.entries(node.parameters).sort(([a], [b]) => a.localeCompare(b))),
  ...(node.position ? { position: { x: node.position.x, y: node.position.y } } : {}),
});
const canonicalConnection = (connection: PatchConnectionV1): PatchConnectionV1 => ({
  id: connection.id,
  source: cloneEndpoint(connection.source),
  target: cloneEndpoint(connection.target),
  ...(connection.feedback === undefined ? {} : { feedback: connection.feedback }),
});
const canonicalPatch = (patch: PatchGraphV1): PatchGraphV1 => ({
  schemaVersion: 1,
  id: patch.id,
  seed: patch.seed,
  nodes: patch.nodes.map(canonicalNode),
  connections: patch.connections.map(canonicalConnection),
});

export const serializePatch = (patch: PatchGraphV1): string => JSON.stringify(canonicalPatch(patch));

export const migratePatch = (input: unknown): PatchGraphV1 => {
  if (!input || typeof input !== 'object') throw new TypeError('patch must be an object');
  const value = input as Record<string, unknown>;
  if (value.schemaVersion === 1) return canonicalPatch(value as unknown as PatchGraphV1);
  if (value.schemaVersion !== 0 || !Array.isArray(value.modules) || !Array.isArray(value.cables)) {
    throw new RangeError('unsupported patch schemaVersion');
  }
  const nodes = value.modules.map((raw): PatchNodeV1 => {
    const node = raw as Record<string, unknown>;
    const hasPosition = Number.isFinite(node.x) && Number.isFinite(node.y);
    return {
      id: String(node.id),
      moduleId: String(node.moduleId),
      parameters: (node.parameters ?? {}) as Readonly<Record<string, number>>,
      ...(hasPosition ? { position: { x: Number(node.x), y: Number(node.y) } } : {}),
    };
  });
  return canonicalPatch({
    schemaVersion: 1,
    id: String(value.id),
    seed: Number(value.randomSeed),
    nodes,
    connections: value.cables as readonly PatchConnectionV1[],
  });
};

export const deserializePatch = (encoded: string): PatchGraphV1 => migratePatch(JSON.parse(encoded) as unknown);
